import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useFormDisable } from './form'
import { FormatLabel } from './format-label'
import { useFormDefaultNull, useFormOnChange, useFormRequired, useFormShouldDirty, useGetWatchValue } from './hooks/input-hooks'

export const FormTextArea = ({
  className,
  label,
  rows,
  autoGrow,
  maxRows,
  placeholder,
  subtext,
  id,
  name,
  required,
  readOnly,
  defaultValue,
  shouldDirty,
  autoFocus,
  hidden,
  small,
  onChange,
  onEnter
}) => {
  const { register } = useFormContext()

  let formDisabled = useFormDisable()

  let watchValue = useGetWatchValue(name)

  useFormDefaultNull(name)
  useFormRequired(name, required)
  useFormShouldDirty(name, defaultValue, shouldDirty)
  useFormOnChange(name, onChange)

  const registration = register(name, { required })

  const handleKeyUp = e => e.code === 'Enter' && onEnter && onEnter()

  // Grow with the content up to maxRows, then scroll.
  //
  // A fixed-height box is unusable for anything paragraph-length — the AI system
  // prompt is several paragraphs with deliberate line breaks, and editing it
  // through a two-row window means scrolling blind. Growing keeps the whole
  // prompt visible while it is short enough to be, and the cap stops one long
  // field from pushing the rest of the form off screen.
  const resize = element => {
    if (!element || !autoGrow) return

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 20
    const padding = element.offsetHeight - element.clientHeight
    const limit = lineHeight * (maxRows || 20) + padding

    // Reset first: without it the height only ever ratchets upward, because
    // scrollHeight can never report less than the current height.
    element.style.height = 'auto'
    element.style.height = `${Math.min(element.scrollHeight + padding, limit)}px`
    element.style.overflowY = element.scrollHeight + padding > limit ? 'auto' : 'hidden'
  }

  // Size on mount and whenever the value changes from outside (the form loads
  // its record after first render, so the initial value arrives late).
  const measuredRef = React.useCallback(
    element => {
      if (element) resize(element)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoGrow, maxRows, watchValue]
  )

  return (
    <React.Fragment>
      <FormatLabel className={className} name={name} label={label} subtext={subtext} hasValue={!!watchValue} required={required} hidden={hidden}>
        <textarea
          id={id}
          className={`form-control ${small && 'form-control-sm'}`}
          placeholder={placeholder || label}
          autoFocus={autoFocus}
          rows={rows}
          style={autoGrow ? { resize: 'vertical' } : undefined}
          {...registration}
          ref={element => {
            // register() supplies its own ref and react-hook-form breaks if it
            // is dropped, so chain rather than replace it.
            registration.ref(element)
            measuredRef(element)
          }}
          disabled={formDisabled}
          readOnly={readOnly}
          onInput={e => resize(e.target)}
          onKeyUp={handleKeyUp}
        />
      </FormatLabel>
    </React.Fragment>
  )
}
